import React, { useEffect } from 'react';
import { Box, VStack, useColorModeValue } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import webCore from '@happylife-a/web-core';
import webApp from '@happylife-a/web-app';
// Custom hooks
import useFileUpload from './hooks/useFileUpload';
import useContentGeneration from './hooks/useContentGeneration';

// Components
import AnimatedBackground from './AnimatedBackground';
import Header from './Header';
import UploadArea from './UploadArea';
import LoadingAnimation from './LoadingAnimation';
import UsageLimitBar from './UsageLimitBar';

// Constants
import { containerVariants } from './constants';

const MotionBox = motion(Box);

export default function AIContentGenerator({ form, onGenerateSuccess }) {
  const { t } = webCore.contexts.useTranslation();
  const toast = webApp.theme.hooks.useToast();
  const { aiUsage, refetchUsages: refetch } = webCore.coreHooks.aiUsages.useAiUsage();
  // Custom hooks
  const {
    selectedFile,
    previewUrl,
    isDragOver,
    fileInputRef,
    handleRemoveImage,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleFileInputChange,
    triggerFileInput,
  } = useFileUpload();

  const onSuccess = () => {
    refetch();
    onGenerateSuccess();
  };

  const { isLoading, loadingProgress, loadingStage, handleGenerateContent, resetGeneration } =
    useContentGeneration({ form, onSuccess });
  // Color mode values
  const bgGradient = useColorModeValue(
    'linear(to-b, primary.500, grey.800)',
    'linear(to-b, primary.500, grey.900)'
  );
  const borderColor = useColorModeValue('white', 'white');

  const validateCategory = () => {
    const categoryId = form.getValues('GeneralInfo._cat.parentCategoryId');

    if (!categoryId) {
      toast({
        status: 'warning',
        title: t('Please select a category'),
      });
      return false;
    }

    return true;
  };

  // Handle drop with content generation
  const handleDropAndGenerate = async (e) => {
    if (!validateCategory() || isLoading) {
      return;
    }

    const result = await handleDrop(e);
    if (result?.success) {
      handleGenerateContent(result.file);
    }
  };

  // Handle file input change with content generation
  const handleFileInputChangeAndGenerate = async (e) => {
    if (!validateCategory() || isLoading) {
      return;
    }

    const result = await handleFileInputChange(e);
    if (result?.success) {
      handleGenerateContent(result.file);
    }
  };

  // Reset generation when file is removed
  const handleRemoveImageAndReset = () => {
    handleRemoveImage();
    resetGeneration();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <MotionBox
      bgGradient={bgGradient}
      borderRadius="xl"
      p={6}
      mb={6}
      boxShadow="lg"
      position="relative"
      overflow="hidden"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Animated background particles */}
      <AnimatedBackground isVisible={isLoading} />

      {/* Main content */}
      <VStack spacing={4} align="center">
        {/* Header */}
        <Header isLoading={isLoading} />

        {/* Usage Limit Bar */}
        {aiUsage && <UsageLimitBar {...aiUsage} onUpgrade={() => {}} />}

        {/* Upload Area */}
        {!aiUsage?.isAtLimit && (
          <UploadArea
            isLoading={isLoading}
            selectedFile={selectedFile}
            previewUrl={previewUrl}
            isDragOver={isDragOver}
            borderColor={borderColor}
            onRemoveImage={handleRemoveImageAndReset}
            onDrop={handleDropAndGenerate}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={triggerFileInput}
          />
        )}

        {/* Loading Animation */}
        <LoadingAnimation
          isLoading={isLoading}
          loadingStage={loadingStage}
          loadingProgress={loadingProgress}
        />

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileInputChangeAndGenerate}
          style={{ display: 'none' }}
        />
      </VStack>
    </MotionBox>
  );
}
